import { createContext, useContext, useCallback } from "react";
import { useLocation, useNavigate, useParams as useRouterParams, useSearchParams as useRouterSearchParams } from "react-router";

/**
 * `next/navigation` shim.
 *
 * 컴포넌트 66개를 그대로 쓰기 위한 다리다. 실제로 필요한 건 놀랄 만큼 적었다 —
 * 전수 조사 결과 **`router.refresh()` 37회와 `usePathname()` 1회가 전부**이고
 * `push`/`replace`는 컴포넌트 층에 하나도 없었다(그건 서버 액션의 redirect가
 * 하던 일이다).
 *
 * ## refresh()가 핵심이다
 *
 * Next에서 `router.refresh()`는 "서버 컴포넌트 데이터를 다시 받아와라"다. SPA에는
 * 서버 렌더가 없으니, **현재 화면이 자기 데이터를 다시 불러오게** 하는 것으로
 * 옮긴다. 화면(라우트 컴포넌트)이 자기 재조회 함수를 여기 등록하고, 컴포넌트가
 * `refresh()`를 부르면 그게 실행된다.
 *
 * 등록이 없으면 **아무 일도 하지 않는다**(조용히). 저장은 이미 성공했고 화면만
 * 갱신이 안 되는 상태라, 여기서 던지면 성공한 조작이 실패로 보인다.
 */

type RefreshFn = () => void | Promise<void>;

const RefreshContext = createContext<RefreshFn | null>(null);

export function RefreshProvider({ refresh, children }: { refresh: RefreshFn; children: React.ReactNode }) {
  return <RefreshContext.Provider value={refresh}>{children}</RefreshContext.Provider>;
}

export function useRouter() {
  const navigate = useNavigate();
  const refresh = useContext(RefreshContext);

  return {
    refresh: useCallback(() => {
      void refresh?.();
    }, [refresh]),
    push: useCallback((href: string) => void navigate(href), [navigate]),
    replace: useCallback((href: string) => void navigate(href, { replace: true }), [navigate]),
    back: useCallback(() => void navigate(-1), [navigate]),
    forward: useCallback(() => void navigate(1), [navigate]),
    prefetch: () => {},
  };
}

export function usePathname(): string {
  return useLocation().pathname;
}

export function useSearchParams() {
  const [params] = useRouterSearchParams();
  return params;
}

export function useParams<T extends Record<string, string | undefined>>(): T {
  return useRouterParams() as T;
}

/**
 * 서버 전용 함수들. SPA에서 불릴 일이 없어야 하지만, 실수로 불렸을 때 조용히
 * 넘어가면 원인을 찾기 어려우므로 **분명하게 던진다.**
 */
export function redirect(href: string): never {
  throw new Error(`redirect(${href})는 SPA에서 쓸 수 없다 — useRouter().push를 쓸 것`);
}

export function notFound(): never {
  throw new Error("notFound()는 SPA에서 쓸 수 없다");
}

/**
 * Next 내부 제어 흐름(redirect/notFound)을 삼키지 않게 다시 던지는 함수.
 * SPA에는 그런 제어 흐름이 없으므로 **아무것도 하지 않는다** — `guarded()`의
 * catch 안에서 불리는데, 여기서 던지면 정상적인 오류 처리가 깨진다.
 *
 * (`src/server/action-error.ts`가 이걸 import 한다. SPA 런타임에는 그 파일이
 * 들어가지 않지만 타입 검사에는 걸려서 shim에도 있어야 한다.)
 */
export function unstable_rethrow(_error: unknown): void {}
